/**
 * Benefits Service
 *
 * API endpoints for investment benefit operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type { InvestmentBenefit, BenefitType, AmountType } from '@medsuite/models';

export interface CreateBenefitDto {
  investment_id: number;
  benefit_type: BenefitType;
  title: string;
  description?: string;
  amount?: number;
  amount_type?: AmountType;
  period_start?: Date | string;
  period_end?: Date | string;
  is_one_time?: boolean;
}

export interface UpdateBenefitDto extends Partial<Omit<CreateBenefitDto, 'investment_id'>> {}

export interface BenefitSummary {
  type1_count: number;
  type2_count: number;
  total_monetary_benefit: number;
  by_type: {
    amount_type: AmountType;
    total: number;
  }[];
}

/**
 * Benefits Service
 */
class BenefitsService {
  /**
   * Get all benefits for an investment
   */
  async getAll(investmentId: number): Promise<ApiResponse<InvestmentBenefit[]>> {
    return apiClient.get<InvestmentBenefit[]>(`/investments/${investmentId}/benefits`);
  }

  /**
   * Get single benefit by ID
   */
  async getById(investmentId: number, benefitId: number): Promise<ApiResponse<InvestmentBenefit>> {
    return apiClient.get<InvestmentBenefit>(`/investments/${investmentId}/benefits/${benefitId}`);
  }

  /**
   * Get benefit summary
   */
  async getSummary(investmentId: number): Promise<ApiResponse<BenefitSummary>> {
    return apiClient.get<BenefitSummary>(`/investments/${investmentId}/benefits/summary`);
  }

  /**
   * Create new benefit
   */
  async create(
    investmentId: number,
    data: Omit<CreateBenefitDto, 'investment_id'>
  ): Promise<ApiResponse<InvestmentBenefit>> {
    return apiClient.post<InvestmentBenefit>(`/investments/${investmentId}/benefits`, {
      ...data,
      investment_id: investmentId,
    });
  }

  /**
   * Update benefit
   */
  async update(
    investmentId: number,
    benefitId: number,
    data: UpdateBenefitDto
  ): Promise<ApiResponse<InvestmentBenefit>> {
    return apiClient.put<InvestmentBenefit>(
      `/investments/${investmentId}/benefits/${benefitId}`,
      data
    );
  }

  /**
   * Delete benefit
   */
  async delete(investmentId: number, benefitId: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/investments/${investmentId}/benefits/${benefitId}`);
  }
}

export const benefitsService = new BenefitsService();
